import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users,
  ChevronDown,
  ChevronUp,
  User,
  PieChart,
  ExternalLink,
} from "lucide-react";
import InfoTooltip from "./InfoTooltip";

const CommitteeCard = ({ committee, isDarkMode }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showAllMembers, setShowAllMembers] = useState(false);
  const navigate = useNavigate();

  if (!committee) return null;

  // Extract leadership
  // Extract leadership
  const chair = committee.members?.find(
    (m) => m.role === "Chair" || m.role === "Chairman",
  );
  const viceChair = committee.members?.find(
    (m) => m.role === "Vice Chair" || m.role === "Vice Chairman",
  );
  const otherMembers =
    committee.members?.filter(
      (m) =>
        m.role !== "Chair" &&
        m.role !== "Chairman" &&
        m.role !== "Vice Chair" &&
        m.role !== "Vice Chairman",
    ) || [];

  // Display only first 6 members initially unless expanding
  const displayedMembers = showAllMembers
    ? otherMembers
    : otherMembers.slice(0, 6);
  const remainingCount = otherMembers.length - displayedMembers.length;

  return (
    <div
      className={`rounded-xl border ${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"} shadow-sm overflow-hidden mb-4`}
    >
      {/* Header - Always Visible */}
      <div
        className={`w-full flex items-center justify-between p-4 transition-colors ${isDarkMode ? "hover:bg-gray-750" : "hover:bg-gray-50"}`}
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div
            className={`p-2 rounded-lg ${isDarkMode ? "bg-blue-900/30" : "bg-blue-100"}`}
          >
            <Users
              className={`w-5 h-5 ${isDarkMode ? "text-blue-400" : "text-blue-600"}`}
            />
          </div>
          <div className="flex-1 min-w-0">
            {/* Committee name - only text links to GGA website */}
            <div className="flex items-center gap-1.5">
              {committee.website_url || committee.gga_committee_id ? (
                <a
                  href={
                    committee.website_url ||
                    `https://www.legis.ga.gov/committees/${(committee.chamber || "house").toLowerCase()}/${committee.gga_committee_id}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className={`font-bold text-lg hover:underline ${isDarkMode ? "text-blue-400 hover:text-blue-300" : "text-blue-600 hover:text-blue-700"}`}
                >
                  {committee.name}
                </a>
              ) : (
                <h3
                  className={`font-bold text-lg ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  {committee.name}
                </h3>
              )}
              {(committee.website_url || committee.gga_committee_id) && (
                <ExternalLink
                  className={`w-4 h-4 flex-shrink-0 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                />
              )}
            </div>
            <div
              className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"} flex items-center gap-2`}
            >
              {committee.chamber} Committee
            </div>
          </div>
        </div>
            {/* View Bills in this Committee */}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (committee.id) {
                    navigate(`/bills?committee=${committee.id}`);
                  } else if (committee.assembly_code) {
                    navigate(`/bills?committee=${encodeURIComponent(committee.assembly_code)}`);
                  } else {
                    navigate(`/bills`);
                  }
                }}
                className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                  isDarkMode
                    ? "bg-blue-900/30 text-blue-300 hover:bg-blue-900/50"
                    : "bg-blue-100 text-blue-700 hover:bg-blue-200"
                }`}
                aria-label="View Assigned Bills"
                title="View Assigned Bills"
              >
                <ExternalLink className="w-3.5 h-3.5 mr-2" />
                View Assigned Bills
              </button>
            </div>
        {/* Expand/collapse button */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className={`p-2 rounded-lg transition-colors ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"}`}
          aria-label={
            isExpanded
              ? "Collapse committee details"
              : "Expand committee details"
          }
        >
          <div
            className={`transform transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
          >
            <ChevronDown
              className={`w-5 h-5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            />
          </div>
        </button>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div
          className={`border-t ${isDarkMode ? "border-gray-700" : "border-gray-100"}`}
        >
          <div className="p-5 space-y-6">
            {/* Description */}
            {committee.description && (
              <div
                className={`text-sm leading-relaxed ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
              >
                {committee.description}
              </div>
            )}

            {/* Party Breakdown Bar */}
            {committee.percentages && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span
                    className={`text-xs font-semibold uppercase tracking-wider ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Committee Composition
                  </span>
                  <div className="flex gap-3 text-xs font-medium">
                    <span className="text-blue-500">
                      {committee.percentages.democrat}% Dem
                    </span>
                    <span className="text-red-500">
                      {committee.percentages.republican}% Rep
                    </span>
                  </div>
                </div>
                <div className="h-2.5 w-full rounded-full flex overflow-hidden bg-gray-200 dark:bg-gray-700">
                  <div
                    style={{ width: `${committee.percentages.democrat}%` }}
                    className="bg-blue-500"
                  />
                  <div
                    style={{ width: `${committee.percentages.independent}%` }}
                    className="bg-gray-400"
                  />
                  <div
                    style={{ width: `${committee.percentages.republican}%` }}
                    className="bg-red-500"
                  />
                </div>
              </div>
            )}

            {/* Leadership Section */}
            {(chair || viceChair) && (
              <div>
                <h4
                  className={`text-sm font-semibold mb-3 ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}
                >
                  <InfoTooltip
                    content="Legislators who direct the committee's agenda and meetings."
                    isDarkMode={isDarkMode}
                    size="sm"
                    underline={true}
                  >
                    Leadership
                  </InfoTooltip>
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {chair && (
                    <CommitteeMemberRow
                      member={chair}
                      role="Chair"
                      isDarkMode={isDarkMode}
                    />
                  )}
                  {viceChair && (
                    <CommitteeMemberRow
                      member={viceChair}
                      role="Vice Chair"
                      isDarkMode={isDarkMode}
                    />
                  )}
                </div>
              </div>
            )}

            {/* Members List */}
            {otherMembers.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4
                    className={`text-sm font-semibold ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}
                  >
                    Members
                  </h4>
                  <span
                    className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                  >
                    {otherMembers.length} members
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {displayedMembers.map((member) => (
                    <CommitteeMemberRow
                      key={member.name}
                      member={member}
                      isDarkMode={isDarkMode}
                    />
                  ))}
                </div>

                {remainingCount > 0 && !showAllMembers && (
                  <button
                    onClick={() => setShowAllMembers(true)}
                    className={`mt-4 w-full py-2 text-sm font-medium rounded-lg transition-colors ${
                      isDarkMode
                        ? "bg-gray-750 hover:bg-gray-700 text-blue-400"
                        : "bg-gray-50 hover:bg-gray-100 text-blue-600"
                    }`}
                  >
                    Show {remainingCount} more members
                  </button>
                )}

                {showAllMembers && (
                  <button
                    onClick={() => setShowAllMembers(false)}
                    className={`mt-4 w-full py-2 text-sm font-medium rounded-lg transition-colors ${
                      isDarkMode
                        ? "hover:bg-gray-750 text-gray-400"
                        : "hover:bg-gray-50 text-gray-500"
                    }`}
                  >
                    Show less
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const CommitteeMemberRow = ({ member, role, isDarkMode }) => {
  const navigate = useNavigate();
  const party = member.party || "";
  const isDemocrat = party.toLowerCase().includes("democrat");
  const isRepublican = party.toLowerCase().includes("republican");

  const getMemberLink = () => {
    // Try to find IDs in various possible locations (flattened or nested)
    const polId =
      member.politicianId ||
      member.politician_id ||
      member.legislator?.politician_id;
    const websiteUrl =
      member.websiteUrl || member.website_url || member.legislator?.website_url;
    const ggaId =
      member.ggaMemberId ||
      member.gga_member_id ||
      member.legislator?.gga_member_id;

    // Use member chamber if available, otherwise default (will be handled by URL structure usually)
    const chamber = member.chamber || member.legislator?.chamber || "Senate";

    if (polId) return `/politicians/${polId}`;
    // Prefer stored website_url, otherwise compute it
    if (websiteUrl) return websiteUrl;
    if (ggaId) {
      const chamberPath =
        chamber && chamber.toLowerCase().includes("house") ? "house" : "senate";
      return `https://www.legis.ga.gov/members/${chamberPath}/${ggaId}`;
    }
    return null;
  };

  const memberLink = getMemberLink();
  const isClickable = !!memberLink;

  const handleMemberClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!memberLink) return;

    if (memberLink.startsWith("http")) {
      window.open(memberLink, "_blank", "noopener,noreferrer");
    } else {
      navigate(memberLink);
    }
  };

  return (
    <div
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={isClickable ? handleMemberClick : undefined}
      onKeyDown={
        isClickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleMemberClick(e);
              }
            }
          : undefined
      }
      className={`flex items-center justify-between p-2.5 rounded-lg border ${
        isDarkMode
          ? "bg-gray-800/50 border-gray-600/30"
          : "bg-white/80 border-gray-200/50"
      } ${isClickable ? "cursor-pointer hover:border-blue-400 hover:shadow-md transition-all" : ""}`}
    >
      <div className="flex items-center space-x-3 flex-1 min-w-0">
        {/* Avatar Section */}
        <div className="relative flex-shrink-0">
          <img
            src={member.photoUrl}
            alt={member.name}
            className="w-10 h-10 rounded-full object-cover bg-gray-300"
            onError={(e) => {
              e.target.style.display = "none";
              const fallback =
                e.target.parentElement.querySelector(".fallback-badge");
              if (fallback) fallback.style.display = "flex";
            }}
          />

          {/* Fallback - hidden by default, shown on error or if no photo */}
          {!member.photoUrl && (
            <div
              className={`fallback-badge absolute inset-0 rounded-full flex items-center justify-center text-xs font-bold ${
                isDemocrat
                  ? isDarkMode
                    ? "bg-blue-600 text-white"
                    : "bg-blue-100 text-blue-800"
                  : isRepublican
                    ? isDarkMode
                      ? "bg-red-600 text-white"
                      : "bg-red-100 text-red-800"
                    : isDarkMode
                      ? "bg-gray-600 text-gray-300"
                      : "bg-gray-100 text-gray-700"
              }`}
            >
              {party ? party.substring(0, 3).toUpperCase() : "?"}
            </div>
          )}

          {/* Fallback div for onError to toggle */}
          <div
            className={`fallback-badge hidden absolute inset-0 rounded-full items-center justify-center text-xs font-bold ${
              isDemocrat
                ? isDarkMode
                  ? "bg-blue-600 text-white"
                  : "bg-blue-100 text-blue-800"
                : isRepublican
                  ? isDarkMode
                    ? "bg-red-600 text-white"
                    : "bg-red-100 text-red-800"
                  : isDarkMode
                    ? "bg-gray-600 text-gray-300"
                    : "bg-gray-100 text-gray-700"
            }`}
          >
            {party ? party.substring(0, 3).toUpperCase() : "?"}
          </div>

          {/* Party Badge Overlay */}
          {party && member.photoUrl && (
            <span
              className={`absolute -bottom-0.5 -right-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold border-2 ${isDarkMode ? "border-gray-800" : "border-white"} ${
                isDemocrat
                  ? "bg-blue-600 text-white"
                  : isRepublican
                    ? "bg-red-600 text-white"
                    : "bg-gray-600 text-white"
              }`}
              title={party}
            >
              {party.substring(0, 1).toUpperCase()}
            </span>
          )}
        </div>

        {/* Info Section */}
        <div className="flex flex-col min-w-0 flex-1">
          <span
            className={`${isDarkMode ? "text-gray-200" : "text-gray-900"} text-sm font-medium truncate ${isClickable ? "hover:underline" : ""}`}
          >
            {member.name}
          </span>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            {/* Role Badge */}
            {role && (
              <span
                className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded border ${
                  role === "Chair"
                    ? isDarkMode
                      ? "bg-blue-900/50 text-blue-300 border-blue-800"
                      : "bg-blue-100 text-blue-700 border-blue-200"
                    : role === "Vice Chair"
                      ? isDarkMode
                        ? "bg-purple-900/50 text-purple-300 border-purple-800"
                        : "bg-purple-100 text-purple-700 border-purple-200"
                      : isDarkMode
                        ? "bg-gray-700 text-gray-400 border-gray-600"
                        : "bg-gray-100 text-gray-600 border-gray-200"
                }`}
              >
                {role}
              </span>
            )}
            {/* District */}
            <span
              className={`text-xs font-medium ${
                isDemocrat
                  ? isDarkMode
                    ? "text-blue-400"
                    : "text-blue-600"
                  : isRepublican
                    ? isDarkMode
                      ? "text-red-400"
                      : "text-red-600"
                    : isDarkMode
                      ? "text-gray-400"
                      : "text-gray-600"
              }`}
            >
              Dist {member.district}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommitteeCard;
